import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { DatePipe, NgClass, NgTemplateOutlet } from '@angular/common';
import {
  Component,
  computed,
  effect,
  EventEmitter,
  inject,
  Input,
  Output,
  signal,
} from '@angular/core';
import { Airport } from '../../models/airport';
import { ClickOutsideDirective } from '../../../../shared/directives/click-outside.directive';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Passenger } from '../../models/passenger';
import { TravelClass } from '../../enums/travel-class.enum';
import { FlightState } from '../../services/flight/flight.state';
import { SameAirportComponent } from '../popups/same-airport/same-airport.component';
import { SearchCriteria, Trip } from '../../models/search-criteria';
import { DeepLinkService } from '../../services/flight/deep-link.service';
import { Router } from '@angular/router';
import { HomeAirportPickerComponent } from './home-airport-picker/home-airport-picker.component';
import { FlightFacade } from '../../services/flight/flight.facade';

/** 9 adults and 9 children at most (each type on its own) */
const MAX_PER_TYPE = 9;
/** Multi City: 2 to 5 flights */
export const MIN_FLIGHTS = 2;
export const MAX_FLIGHTS = 5;
/** Direct, 1 stop, 2+ stops: the values of the Stops filter */
export const ALL_STOPS = [0, 1, 2];

export type DateField = 'departDate' | 'returnDate';

/** a calendar open: which field, the month shown first (YYYY-MM), the day picked but not applied */
export interface Cal {
  index: number;
  field: DateField;
  ym: string;
  tmp: string | null;
}

export interface CalCell {
  day: number;
  iso: string;
  out: boolean;
  dis: boolean;
  on: boolean;
  rng: boolean;
  tod: boolean;
}

@Component({
  selector: 'app-flight-search',
  imports: [
    ClickOutsideDirective,
    NgClass,
    NgTemplateOutlet,
    DatePipe,
    HomeAirportPickerComponent,
    ReactiveFormsModule,
    TranslateModule,
    SameAirportComponent,
  ],
  templateUrl: './flight-search.component.html',
  styleUrl: './flight-search.component.css',
})
export class FlightSearchComponent {
  private translate = inject(TranslateService);
  private router = inject(Router);
  private deepLinkService = inject(DeepLinkService);
  private flightState = inject(FlightState);
  private fb = inject(FormBuilder);
  private flightFacade = inject(FlightFacade);

  searchCriteria = computed(() => this.flightState.searchCriteria());
  flightSearchLoading = computed(() => this.flightState.flightSearchLoading());

  @Input() label: 'search' | 'modify' = 'search';
  @Output() close = new EventEmitter<'search' | 'filter'>();

  readonly searchTabs = [
    { key: 'Search.Round_Trip', value: 'Round Trip' },
    { key: 'Search.One_Way', value: 'One Way' },
    { key: 'Search.Multi_City', value: 'Multi City' },
  ];

  readonly classes = [
    { value: TravelClass.ECONOMY, label: 'Search.Economy' },
    { value: TravelClass.PREMIUM_ECONOMY, label: 'Search.Premium_Economy' },
    { value: TravelClass.BUSINESS, label: 'Search.Business' },
    { value: TravelClass.FIRST, label: 'Search.First' },
  ];

  readonly allStops = [
    { value: 0, label: 'Flights.Results.Direct' },
    { value: 1, label: 'Flights.Results.One_Stop' },
    { value: 2, label: 'Flights.Results.Two_Plus' },
  ];
  stopSet = signal<Set<number>>(new Set(ALL_STOPS));

  readonly maxFlights = MAX_FLIGHTS;
  readonly minFlights = MIN_FLIGHTS;
  readonly weekDays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  cal = signal<Cal | null>(null);

  /** the calendar's months: two (one on the results page's phones; the prototype's monthGrid) */
  months = computed(() => {
    const c = this.cal();
    if (!c) return [];
    const count = this.calMonthCount;
    const min = this.minOf(c.index, c.field);
    const today = toIso(new Date());
    const depart = this.getTripControl(c.index, 'departDate')?.value;
    return Array.from({ length: count }, (_, k) => {
      const ym = ymAdd(c.ym, k);
      const [y, m] = ym.split('-').map(Number);
      const first = new Date(y, m - 1, 1);
      const days = new Date(y, m, 0).getDate();
      const lead = first.getDay();
      const cells: CalCell[] = [];
      for (let i = 0; i < lead; i++) {
        const d = new Date(y, m - 1, 1 - lead + i);
        cells.push({ day: d.getDate(), iso: '', out: true, dis: true, on: false, rng: false, tod: false });
      }
      for (let d = 1; d <= days; d++) {
        const iso = `${y}-${pad2(m)}-${pad2(d)}`;
        cells.push({
          day: d,
          iso,
          out: false,
          dis: iso < min,
          // the return's calendar also marks the departure day
          on: iso === c.tmp || (c.field === 'returnDate' && iso === depart),
          rng: c.field === 'returnDate' && !!depart && !!c.tmp && iso > depart && iso < c.tmp,
          tod: iso === today,
        });
      }
      while (cells.length % 7) {
        cells.push({ day: cells.length % 7, iso: '', out: true, dis: true, on: false, rng: false, tod: false });
      }
      return { ym, first, cells };
    });
  });

  selectedTab = 'Round Trip';
  searchForm!: FormGroup;
  passengers: Passenger = { adult: 1, child: 0, infant: 0 };
  readonly paxTypes: { key: keyof Passenger; label: string }[] = [
    { key: 'adult', label: 'Passenger.Adults' },
    { key: 'child', label: 'Passenger.Children' },
    { key: 'infant', label: 'Passenger.Infants' },
  ];
  isSubmitted = false;
  sameAirportPopup = false;

  fromCities: string[] = [];
  toCities: string[] = [];

  dropdownStates: {
    from: boolean;
    to: boolean;
  }[] = [{ from: false, to: false }];

  globalDropdowns = {
    passenger: false,
    class: false,
    stop: false,
  };

  constructor() {
    effect(() => {
      let searchCriteria = this.flightState.searchCriteria();

      this.setupInitialPassengerData();
    });
    this.initForm();
  }

  ngOnInit(): void {
    this.setupInitialPassengerData();
  }

  closeSearchModal() {
    this.close.emit('search');
  }

  initForm() {
    this.searchForm = this.fb.group({
      classType: ['ECONOMY', Validators.required],
      passenger: { adult: 1, child: 0, infant: 0 },
      maxNumberOfConnections: [null],
      trips: this.fb.array([this.createTripGroup()]),
    });
  }

  createTripGroup(): FormGroup {
    return this.fb.group({
      departureAirport: [null, Validators.required],
      arrivalAirport: [null, Validators.required],
      departDate: ['', Validators.required],
      returnDate: [''],
    });
  }

  setupInitialPassengerData() {
    if (this.label === 'modify') {
      this.handleModifyMode();
    } else {
      this.onTabChange(this.searchTabs[0].value);
    }
  }

  handleModifyMode() {
    const criteria = this.searchCriteria();
    if (criteria) {
      this.patchSearchForm(criteria);
    }
  }

  patchSearchForm(data: SearchCriteria) {
    const tripsArray = this.trips;

    tripsArray.clear();

    data.formValue.trips.forEach(() => {
      tripsArray.push(this.createTripGroup());
    });

    this.searchForm.patchValue({
      trips: data.formValue.trips,
      maxNumberOfConnections: data.formValue.maxNumberOfConnections,
      classType: data.formValue.classType,
    });
    this.stopsFromMax(data.formValue.maxNumberOfConnections);

    data.formValue.trips.forEach((trip: Trip, index: number) => {
      this.fromCities[index] = trip.departureAirport
        ? `${trip.departureAirport.city} (${trip.departureAirport.iata})`
        : '';

      this.toCities[index] = trip.arrivalAirport
        ? `${trip.arrivalAirport.city} (${trip.arrivalAirport.iata})`
        : '';

      this.dropdownStates[index] = {
        from: false,
        to: false,
      };

      if (data.tripType === 'Multi City') {
        this.updateReturnDateValidator(index, false);
      }
    });

    this.passengers = structuredClone(data.passengers);
    this.selectedTab = data.tripType;
  }

  onTabChange(tab: string) {
    const from = this.selectedTab;
    const first = this.trips.at(0)?.value;
    const second = this.trips.at(1)?.value;

    this.selectedTab = tab;
    switch (tab) {
      case 'One Way':
        this.clearReturnDate();
        this.removeExtraTrips();
        this.updateReturnDateValidator(0, false);
        break;

      case 'Round Trip':
        this.removeExtraTrips();
        this.updateReturnDateValidator(0, true);
        break;

      case 'Multi City':
        this.clearReturnDate();
        this.trips.controls.forEach((_, index) => {
          this.updateReturnDateValidator(index, false);
        });
        break;
    }
    if (tab === from) return;

    if (tab === 'Multi City') {
      this.seedMultiCity(first);
    } else if (tab === 'Round Trip' && from === 'Multi City' && second?.departDate) {
      // back to a round trip: the second flight's date becomes the return
      const ret = second.departDate < first?.departDate ? first.departDate : second.departDate;
      this.getTripControl(0, 'returnDate').setValue(ret);
    }

    // a round trip always has a return date: a week after the departure when none was chosen yet
    if (tab === 'Round Trip') {
      const depart = this.getTripControl(0, 'departDate')?.value;
      const ret = this.getTripControl(0, 'returnDate');
      if (depart && !ret?.value) ret.setValue(addDays(depart, 7));
    }
  }

  // ─── Multi City: 2 to 5 flights ───

  /** Multi City starts from the current search: flight 1 = From → To, flight 2 = the way back */
  private seedMultiCity(first: any) {
    while (this.trips.length < MIN_FLIGHTS) {
      const i = this.trips.length;
      this.addTrip();
      this.updateReturnDateValidator(i, false);
      if (i === 1 && first) {
        this.trips.at(1).patchValue({
          departureAirport: first.arrivalAirport ?? null,
          arrivalAirport: first.departureAirport ?? null,
          departDate:
            first.returnDate ||
            (first.departDate ? addDays(first.departDate, 7) : ''),
        });
        this.fromCities[1] = this.toCities[0] ?? '';
        this.toCities[1] = this.fromCities[0] ?? '';
      }
    }
  }

  /** a new flight starts from the previous destination, 3 days later, and opens its To field */
  addFlight() {
    if (this.trips.length >= MAX_FLIGHTS) return;
    const prev = this.trips.at(this.trips.length - 1).value;
    const i = this.trips.length;
    this.addTrip();
    this.updateReturnDateValidator(i, false);
    this.trips.at(i).patchValue({
      departureAirport: prev.arrivalAirport ?? null,
      departDate: prev.departDate ? addDays(prev.departDate, 3) : '',
    });
    this.fromCities[i] = this.toCities[i - 1] ?? '';
    setTimeout(() => this.onFocusInput('to', i));
  }

  removeFlight(index: number) {
    if (this.trips.length <= MIN_FLIGHTS) return;
    this.closeAirports();
    this.removeTrip(index);
  }

  get trips() {
    return this.searchForm.get('trips') as FormArray;
  }

  getTripControl(index: number, controlName: string): FormControl {
    return this.trips.at(index).get(controlName) as FormControl;
  }

  addTripState() {
    this.fromCities.push('');
    this.toCities.push('');

    this.dropdownStates.push({
      from: false,
      to: false,
    });
  }

  addTrip(): void {
    this.trips.push(this.createTripGroup());
    this.addTripState();
  }

  removeTrip(index: number): void {
    this.trips.removeAt(index);
    this.dropdownStates.splice(index, 1);
    this.fromCities.splice(index, 1);
    this.toCities.splice(index, 1);
  }

  removeExtraTrips(): void {
    const trips = this.trips;

    while (trips.length > 1) {
      trips.removeAt(1);

      this.dropdownStates.splice(1, 1);
      this.fromCities.splice(1, 1);
      this.toCities.splice(1, 1);
    }
  }

  clearReturnDate(): void {
    const control = this.getTripControl(0, 'returnDate');
    control.setValue('');
    control.updateValueAndValidity();
  }

  onFocusInput(type: 'from' | 'to', index: number) {
    if (!this.dropdownStates[index]) {
      this.dropdownStates[index] = {
        from: false,
        to: false,
      };
    }

    this.dropdownStates[index].from = type === 'from';
    this.dropdownStates[index].to = type === 'to';
  }

  // ─── From / To: the airport picker over the field ───

  closeAirports() {
    this.dropdownStates.forEach((d) => (d.from = d.to = false));
  }

  isAirportOpen(index: number, type: 'from' | 'to'): boolean {
    return !!this.dropdownStates[index]?.[type];
  }

  /** a click on an open From / To closes it */
  toggleAirport(type: 'from' | 'to', index: number) {
    if (this.isAirportOpen(index, type)) {
      this.closeAirports();
      return;
    }
    this.closeAirports();
    this.onFocusInput(type, index);
  }

  airportOf(index: number, type: 'from' | 'to'): Airport | null {
    return (
      this.getTripControl(
        index,
        type === 'from' ? 'departureAirport' : 'arrivalAirport'
      )?.value ?? null
    );
  }

  /** "Milan, Italy", or "Where to go?" */
  apText(index: number, type: 'from' | 'to'): string {
    const a = this.airportOf(index, type);
    if (!a?.iata) return this.translate.instant('Search.Where_To_Go');
    return `${a.city}, ${a.country}`;
  }

  /** home: From opens To, To opens Depart Date */
  onAirportPicked(event: {
    type: 'from' | 'to';
    airport: Airport;
    index: number;
  }) {
    this.onSelectedCity(event);
    if (event.type === 'from') {
      setTimeout(() => this.onFocusInput('to', event.index));
    } else {
      setTimeout(() => this.toggleCal(event.index, 'departDate'));
    }
  }

  selectedCity(event: {
    type: 'from' | 'to';
    airport: Airport;
    index: number;
  }): string {
    const controlName =
      event.type === 'from' ? 'departureAirport' : 'arrivalAirport';
    this.getTripControl(event.index, controlName).setValue(event.airport);

    return `${event.airport?.city} (${event.airport?.iata})`;
  }

  onSelectedCity(event: {
    type: 'from' | 'to';
    airport: Airport;
    index: number;
  }) {
    const formattedCity = this.selectedCity(event);

    if (event.type === 'from') {
      this.fromCities[event.index] = formattedCity;
      this.dropdownStates[event.index].from = false;
    } else {
      this.toCities[event.index] = formattedCity;
      this.dropdownStates[event.index].to = false;
    }
  }

  removeSelectedCity(cityType: 'from' | 'to', index: number) {
    const tripGroup = this.trips.at(index);
    if (cityType === 'from') {
      this.fromCities[index] = '';
      this.dropdownStates[index].from = true;
      tripGroup.get('departureAirport')?.reset(null);
    } else {
      this.toCities[index] = '';
      this.dropdownStates[index].to = true;
      tripGroup.get('arrivalAirport')?.reset(null);
    }
  }

  swapCities(index: number): void {
    const departureAirport = this.getTripControl(
      index,
      'departureAirport'
    )?.value;
    const arrivalAirport = this.getTripControl(index, 'arrivalAirport')?.value;

    this.trips.at(index).patchValue({
      departureAirport: arrivalAirport,
      arrivalAirport: departureAirport,
    });

    [this.fromCities[index], this.toCities[index]] = [
      this.toCities[index],
      this.fromCities[index],
    ];
  }

  updateReturnDateValidator(index: number, required: boolean) {
    const returnDateControl = this.getTripControl(index, 'returnDate');
    returnDateControl.setValidators(required ? [Validators.required] : []);
    returnDateControl.updateValueAndValidity();
  }

  get selectedClass(): string {
    return this.searchForm.get('classType')?.value;
  }

  get selectedClassLabel(): string {
    return (
      this.classes.find((c) => c.value === this.selectedClass)?.label ?? ''
    );
  }

  onClassChange(className: string) {
    this.searchForm.get('classType')?.setValue(className);
    this.globalDropdowns.class = false;
  }

  // ─── stops: several at once, the last one can't be unchecked ───

  /** "All flights" or "Direct, 1 stop" */
  get stopsText(): string {
    if (this.stopSet().size === ALL_STOPS.length) return this.translate.instant('Search.All_Flights');
    return this.allStops
      .filter((s) => this.isStop(s.value))
      .map((s) => this.translate.instant(s.label))
      .join(', ');
  }

  isStop(value: number): boolean {
    return this.stopSet().has(value);
  }

  toggleStop(value: number) {
    const next = new Set(this.stopSet());
    if (next.has(value)) {
      if (next.size === 1) return;
      next.delete(value);
    } else {
      next.add(value);
    }
    this.stopSet.set(next);
  }

  /** the stops of a search's maximum of connections (none = all of them) */
  protected stopsFromMax(max: number | null | undefined) {
    this.stopSet.set(
      new Set(max == null ? ALL_STOPS : ALL_STOPS.filter((v) => v <= max))
    );
  }

  get isMulti(): boolean {
    return this.selectedTab === 'Multi City';
  }

  /** a calendar opens: the other dropdowns close */
  protected closeOthers() {
    this.closeAirports();
    this.globalDropdowns.passenger = false;
    this.globalDropdowns.class = false;
    this.globalDropdowns.stop = false;
  }

  /** months the calendar shows side by side */
  protected get calMonthCount(): number {
    return 2;
  }

  // ─── calendar (the prototype's popCal) ───────────────────────

  isCal(index: number, field: DateField): boolean {
    const c = this.cal();
    return !!c && c.index === index && c.field === field;
  }

  /** opens on the chosen date's month, with that date picked */
  toggleCal(index: number, field: DateField) {
    if (this.isCal(index, field)) {
      this.closeCal();
      return;
    }
    this.closeOthers();
    const tmp = this.getTripControl(index, field)?.value || this.minOf(index, field);
    // the return opens on the departure's month when both fit in the months shown (31 Oct and 1 Nov together)
    const depart = this.getTripControl(index, 'departDate')?.value;
    const fromDepart =
      field === 'returnDate' &&
      !!depart &&
      ymAdd(depart.slice(0, 7), this.calMonthCount - 1) >= tmp.slice(0, 7);
    this.cal.set({ index, field, ym: (fromDepart ? depart : tmp).slice(0, 7), tmp });
  }

  closeCal() {
    this.cal.set(null);
  }

  /** the first day that can be picked: today; the return never before the departure; a flight never before the previous one */
  protected minOf(index: number, field: DateField): string {
    const today = toIso(new Date());
    if (field === 'returnDate') return this.getTripControl(index, 'departDate')?.value || today;
    const prev = this.minDateOf(index);
    return prev && prev > today ? prev : today;
  }

  get canPrevMonth(): boolean {
    const c = this.cal();
    return !!c && c.ym > toIso(new Date()).slice(0, 7);
  }

  moveMonth(step: number) {
    const c = this.cal();
    if (c) this.cal.set({ ...c, ym: ymAdd(c.ym, step) });
  }

  pickDay(iso: string) {
    const c = this.cal();
    if (c) this.cal.set({ ...c, tmp: iso });
  }

  /** Apply: the date is set; the departure of a round trip then opens the return */
  applyCal() {
    const c = this.cal();
    if (!c?.tmp) return;
    this.getTripControl(c.index, c.field).setValue(c.tmp);
    if (c.field === 'departDate') {
      const ret = this.getTripControl(c.index, 'returnDate');
      if (ret?.value && ret.value < c.tmp) ret.setValue(c.tmp);
    }
    if (this.isMulti) this.pushLaterFlights(c.index);
    if (c.field === 'departDate' && this.selectedTab === 'Round Trip' && !this.isMulti) {
      this.cal.set(null);
      this.toggleCal(c.index, 'returnDate');
    } else {
      this.cal.set(null);
    }
  }

  /** ‹ › one day back / forward (never before the first possible day); closes any dropdown */
  shiftDay(index: number, field: DateField, days: number) {
    this.shiftDate(index, field, days);
    this.cal.set(null);
    this.closeOthers();
  }

  /** a flight's date can't be before the previous one: later flights move along */
  protected pushLaterFlights(index: number) {
    for (let j = index + 1; j < this.trips.length; j++) {
      const prev = this.getTripControl(j - 1, 'departDate').value;
      const control = this.getTripControl(j, 'departDate');
      if (prev && control.value && control.value < prev) control.setValue(prev);
    }
  }

  /** the first day a flight can leave: today, or the previous flight's day */
  minDateOf(index: number): string | null {
    if (!this.isMulti || index === 0) return null;
    return this.getTripControl(index - 1, 'departDate').value || null;
  }

  /** ‹ › one day back / forward; never in the past, the return never before the departure */
  shiftDate(index: number, field: 'departDate' | 'returnDate', days: number) {
    if (!this.canShift(index, field, days)) return;
    const control = this.getTripControl(index, field);
    const next = addDays(control.value, days);

    control.setValue(next);
    const ret = this.getTripControl(index, 'returnDate');
    if (field === 'departDate' && ret.value && ret.value < next) {
      ret.setValue(next);
    }
    if (this.isMulti) this.pushLaterFlights(index);
  }

  canShift(
    index: number,
    field: 'departDate' | 'returnDate',
    days: number
  ): boolean {
    const value = this.getTripControl(index, field).value;
    if (!value || days > 0) return !!value;

    const prev = addDays(value, -1);
    if (prev < toIso(new Date())) return false;

    if (field === 'returnDate') {
      const depart = this.getTripControl(index, 'departDate').value;
      return !depart || prev >= depart;
    }
    const min = this.minDateOf(index);
    return !min || prev >= min;
  }

  // ─── travellers: 1–9 adults, no more infants than adults, 9 seats ───

  /** up to 9 adults and up to 9 children (each on its own); never more infants than adults */
  canIncrease(type: keyof Passenger): boolean {
    if (type === 'infant') return this.passengers.infant < this.passengers.adult;
    return this.passengers[type] < MAX_PER_TYPE;
  }

  canDecrease(type: keyof Passenger): boolean {
    return type === 'adult'
      ? this.passengers.adult > 1
      : this.passengers[type] > 0;
  }

  paxLabel(key: keyof Passenger, n: number): string {
    if (key === 'adult') return n > 1 ? 'Passenger.Adults' : 'Passenger.Adult';
    if (key === 'child') return n === 1 ? 'Passenger.Child' : 'Passenger.Children';
    return n === 1 ? 'Passenger.Infant' : 'Passenger.Infants';
  }

  increase(type: keyof Passenger): void {
    if (this.canIncrease(type)) this.passengers[type]++;
  }

  decrease(type: keyof Passenger): void {
    if (
      this.passengers[type] === 0 ||
      (type === 'adult' && this.passengers.adult === 1)
    ) {
      return;
    }

    this.passengers[type]--;

    if (type === 'adult' && this.passengers.infant > this.passengers.adult) {
      this.passengers.infant = this.passengers.adult;
    }
  }

  get passengerSummary(): string[] {
    this.passengers = this.passengers ?? { adult: 1, child: 0, infant: 0 };
    const { adult, child, infant } = this.passengers;

    const adultText =
      adult === 1
        ? this.translate.instant('Passenger.Adult')
        : this.translate.instant('Passenger.Adults');
    const childText =
      child === 1
        ? this.translate.instant('Passenger.Child')
        : this.translate.instant('Passenger.Children');
    const infantText =
      infant === 1
        ? this.translate.instant('Passenger.Infant')
        : this.translate.instant('Passenger.Infants');

    return [
      `${adult || 0} ${adultText}, ${child || 0} ${childText}, ${
        infant || 0
      } ${infantText}`,
    ];
  }

  validateTrips(): boolean {
    const trips = this.searchForm.value.trips;

    for (let trip of trips) {
      const departureAirport = trip.departureAirport;
      const arrivalAirport = trip.arrivalAirport;

      if (!this.hasDuplicateAirports(departureAirport, arrivalAirport)) {
        return false;
      }

      this.getSavedCities(departureAirport, arrivalAirport);
    }

    return true;
  }

  buildPayload() {
    const formValue = this.searchForm.value;

    return {
      formValue,
      passengers: this.passengers,
      tripType: this.selectedTab,
    };
  }

  private getSearchUrl(url: string): string {
    const parsed = new URL(url, window.location.origin);

    parsed.searchParams.delete('currency');
    parsed.searchParams.delete('lang');

    return parsed.pathname + '?' + parsed.searchParams.toString();
  }

  onSearch() {
    this.isSubmitted = true;
    this.searchForm.markAllAsTouched();

    // the backend search takes a maximum of connections; the exact choice is applied as the Stops filter
    const stops = [...this.stopSet()].sort();
    const all = stops.length === ALL_STOPS.length;
    const max = all || stops.includes(2) ? null : Math.max(...stops);
    this.searchForm.get('maxNumberOfConnections')?.setValue(max);

    if (this.searchForm.invalid || !this.validateTrips()) {
      return;
    }
    this.flightState.removeflightResponse();
    this.flightState.pendingStops.set(
      all ? null : stops.flatMap((v) => (v === 2 ? [2, 3, 4] : [v]))
    );

    const rawValue = this.searchForm.value;
    const searchData = {
      tripType: this.selectedTab,
      ...rawValue,
    };

    sessionStorage.setItem('flightSearchData', JSON.stringify(searchData));
    this.close.emit();
    const payload = this.buildPayload();

    const url = this.deepLinkService.buildResultsUrl(payload);

    this.router.navigateByUrl(url, {
      state: {
        searchData: payload,
      },
    });
  }

  getSavedCities(fromCity: Airport, toCity: Airport) {
    const stored = sessionStorage.getItem('recentCities');

    let recent = stored ? JSON.parse(stored) : [];

    [fromCity, toCity].forEach((city) => {
      if (!city) return;
      recent = recent.filter((a: Airport) => a.iata !== city.iata);
      recent.unshift(city);
    });

    if (recent.length > 7) recent = recent.slice(0, 7);

    sessionStorage.setItem('recentCities', JSON.stringify(recent));
  }

  hasDuplicateAirports(departureAirport: any, arrivalAirport: any) {
    if (departureAirport?.id === arrivalAirport?.id) {
      this.sameAirportPopup = true;
      return false;
    }

    return true;
  }

  closePopup() {
    this.sameAirportPopup = false;
  }
}

/** "YYYY-MM-DD" of a local day */
export function toIso(date: Date): string {
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${m}-${d}`;
}

export function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/** "YYYY-MM" plus n months */
export function ymAdd(ym: string, n: number): string {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(y, m - 1 + n, 1);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
}

export function addDays(iso: string, days: number): string {
  const date = new Date(`${iso}T00:00:00`);
  date.setDate(date.getDate() + days);
  return toIso(date);
}
